/**
 * TTS translation: when notes language ≠ TTS language, translate notes before speaking.
 * Uses MyMemory API (free tier: 500 bytes/request, chunked). In-memory cache by source|target|text.
 */

const MYMEMORY_BASE = 'https://api.mymemory.translated.net/get';
/** MyMemory max is 500 bytes per request; chunk by chars to stay under (UTF-8 safe). */
const CHUNK_CHARS = 400;

const cache = new Map<string, string>();

function cacheKey(sourceLang: string, targetLang: string, text: string): string {
  return `${sourceLang}|${targetLang}|${text}`;
}

/**
 * Split text into chunks of at most CHUNK_CHARS, preferring word boundaries.
 */
function chunkText(text: string): string[] {
  const chunks: string[] = [];
  let rest = text.trim();
  while (rest.length > 0) {
    if (rest.length <= CHUNK_CHARS) {
      chunks.push(rest);
      break;
    }
    const slice = rest.slice(0, CHUNK_CHARS);
    const lastSpace = slice.lastIndexOf(' ');
    const splitAt = lastSpace > CHUNK_CHARS / 2 ? lastSpace + 1 : CHUNK_CHARS;
    chunks.push(rest.slice(0, splitAt).trim());
    rest = rest.slice(splitAt).trim();
  }
  return chunks.filter((c) => c.length > 0);
}

/**
 * Translate a single segment via MyMemory. Returns translated text or the original on failure.
 */
async function translateSegment(segment: string, sourceLang: string, targetLang: string): Promise<string> {
  const key = cacheKey(sourceLang, targetLang, segment);
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const url = `${MYMEMORY_BASE}?${new URLSearchParams({
    q: segment,
    langpair: `${sourceLang}|${targetLang}`
  })}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return segment;
    const data = (await res.json()) as { responseData?: { translatedText?: string } };
    const translated = data.responseData?.translatedText;
    const out = typeof translated === 'string' && translated.trim() ? translated.trim() : segment;
    cache.set(key, out);
    return out;
  } catch {
    return segment;
  }
}

/**
 * Translate full text from sourceLang to targetLang. Chunks by CHUNK_CHARS, caches per chunk.
 * Returns a Promise that resolves to the translated string (or original text on failure).
 */
export async function translateNotes(
  text: string,
  sourceLang: string,
  targetLang: string
): Promise<string> {
  if (!text || !sourceLang || !targetLang) return text;
  const s = sourceLang.toLowerCase().substring(0, 2);
  const t = targetLang.toLowerCase().substring(0, 2);
  if (s === t) return text;

  const chunks = chunkText(text);
  if (chunks.length === 0) return text;

  const results = await Promise.all(chunks.map((chunk) => translateSegment(chunk, s, t)));
  return results.join(' ');
}
